package com.sweetlime.bingeamigo.presentation.features.moviedetail

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.StarBorder
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.BorderStroke
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.sweetlime.bingeamigo.model.Movie
import com.sweetlime.bingeamigo.model.MovieDetail
import com.sweetlime.bingeamigo.model.UserReview
import com.sweetlime.bingeamigo.model.toLibraryMovie
import com.sweetlime.bingeamigo.model.types.ContentType
import com.sweetlime.bingeamigo.presentation.components.DetailRow
import com.sweetlime.bingeamigo.presentation.components.FavoriteButton
import com.sweetlime.bingeamigo.presentation.components.FriendReviewCard
import com.sweetlime.bingeamigo.presentation.components.InfoChip
import com.sweetlime.bingeamigo.presentation.components.RateReviewSheet
import com.sweetlime.bingeamigo.presentation.components.WatchedButton
import com.sweetlime.bingeamigo.presentation.components.WatchlistButton
import com.sweetlime.bingeamigo.presentation.features.library.LibraryViewModel
import com.sweetlime.bingeamigo.presentation.features.review.FriendReviewsViewModel
import com.sweetlime.bingeamigo.presentation.features.review.ReviewViewModel
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.util.computeDisplayRating
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MovieDetailContent(
    movie: Movie,
    onBackClick: () -> Unit,
    viewModel: MovieDetailViewModel = koinViewModel(),
    reviewViewModel: ReviewViewModel = koinViewModel(),
    libraryViewModel: LibraryViewModel = koinViewModel(),
    friendReviewsViewModel: FriendReviewsViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val reviewUiState by reviewViewModel.uiState.collectAsStateWithLifecycle()
    val contentSummary by reviewViewModel.contentSummary.collectAsStateWithLifecycle()
    val watched by libraryViewModel.watched.collectAsStateWithLifecycle()
    val friendReviewsState by friendReviewsViewModel.state.collectAsStateWithLifecycle()
    val userReview = watched.find { it.id == movie.id }?.userReview
        ?: reviewUiState.existingReview?.review
    var showRateSheet by remember { mutableStateOf(false) }

    LaunchedEffect(movie.id) {
        viewModel.load(movie.id)
        friendReviewsViewModel.load(ContentType.MOVIE, movie.id.toString())
    }
    LaunchedEffect(reviewUiState.saved) {
        if (reviewUiState.saved) {
            showRateSheet = false
            reviewViewModel.reset()
        }
    }

    if (showRateSheet && state is MovieDetailState.Success) {
        val libraryMovie = (state as MovieDetailState.Success).detail.toLibraryMovie()
        RateReviewSheet(
            title = movie.title,
            initialRating = reviewUiState.existingReview?.rating ?: 0.0,
            initialReview = reviewUiState.existingReview?.review,
            isLoading = reviewUiState.isLoading,
            isSaving = reviewUiState.isSaving,
            onDismiss = {
                showRateSheet = false
                reviewViewModel.reset()
            },
            onSubmit = { rating, text ->
                reviewViewModel.submitMovieReview(libraryMovie, rating, text)
            }
        )
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {
        when (state) {
            is MovieDetailState.Loading -> {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
            }
            is MovieDetailState.Error -> {
                Text(
                    text = (state as MovieDetailState.Error).message,
                    color = Color.White,
                    modifier = Modifier.align(Alignment.Center)
                )
            }
            is MovieDetailState.Success -> {
                val detail = (state as MovieDetailState.Success).detail
                MovieDetailLoaded(
                    movie = movie,
                    detail = detail,
                    userRating = reviewUiState.existingReview?.rating ?: 0.0,
                    userReview = userReview,
                    communityAvgRating = contentSummary.avgRating,
                    friendReviews = friendReviewsState.reviews,
                    likedReviewIds = friendReviewsState.likedReviewIds,
                    onLikeReview = { friendReviewsViewModel.toggleLike(it) },
                    onRateClick = {
                        reviewViewModel.load(ContentType.MOVIE, movie.id.toString())
                        showRateSheet = true
                    }
                )
            }
        }

        // Static top bar — always visible, never scrolls
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(
                onClick = onBackClick,
                modifier = Modifier.background(Color.Black.copy(alpha = 0.5f), CircleShape)
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = "Back",
                    tint = Color.White
                )
            }
            if (state is MovieDetailState.Success) {
                FavoriteButton(
                    libraryMovie = (state as MovieDetailState.Success).detail.toLibraryMovie()
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun MovieDetailLoaded(
    movie: Movie,
    detail: MovieDetail,
    userRating: Double,
    userReview: String?,
    communityAvgRating: Double,
    friendReviews: List<UserReview>,
    likedReviewIds: Set<String> = emptySet(),
    onLikeReview: (UserReview) -> Unit = {},
    onRateClick: () -> Unit
) {
    val backdropUrl = "https://image.tmdb.org/t/p/w780${detail.backdropPath ?: movie.backdropPath}"
    val posterUrl = "https://image.tmdb.org/t/p/w500${detail.posterPath ?: movie.posterPath}"
    val libraryMovie = detail.toLibraryMovie()

    LazyColumn {

        // Hero section
        item {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 380.dp)
            ) {
                KamelImage(
                    resource = asyncPainterResource(backdropUrl),
                    contentDescription = detail.title,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.matchParentSize()
                )

                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .background(
                            Brush.verticalGradient(
                                colorStops = arrayOf(
                                    0.25f to Color.Transparent,
                                    1.0f to Color(0xFF121212)
                                )
                            )
                        )
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 130.dp, start = 20.dp, end = 20.dp, bottom = 20.dp),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Card(
                        shape = RoundedCornerShape(16.dp),
                        elevation = CardDefaults.cardElevation(8.dp)
                    ) {
                        KamelImage(
                            resource = asyncPainterResource(posterUrl),
                            contentDescription = detail.title,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier
                                .width(110.dp)
                                .height(165.dp)
                        )
                    }

                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = detail.title,
                            color = Color.White,
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )

                        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            if (detail.releaseDate.length >= 4) {
                                InfoChip(text = detail.releaseDate.take(4))
                            }
                            InfoChip(text = "⭐ ${computeDisplayRating(detail.voteAverage, communityAvgRating.takeIf { it > 0.0 }).format1f()}")
                            if (detail.status.isNotBlank()) {
                                InfoChip(text = detail.status)
                            }
                        }

                        if (detail.runtime > 0 || detail.genres.isNotEmpty()) {
                            FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                if (detail.runtime > 0) {
                                    InfoChip(text = "${detail.runtime} min")
                                }
                                detail.genres.firstOrNull()?.let {
                                    InfoChip(text = it.name)
                                }
                            }
                        }

                        Column(modifier = Modifier.width(200.dp)) {
                            WatchedButton(libraryMovie)
                            WatchlistButton(libraryMovie)
                            Button(
                                onClick = onRateClick,
                                modifier = Modifier.fillMaxWidth(),
                                colors = if (userRating > 0.0)
                                    ButtonDefaults.buttonColors(containerColor = BrandColors.Primary, contentColor = Color.Black)
                                else
                                    ButtonDefaults.buttonColors(containerColor = Color.Transparent, contentColor = Color.White),
                                border = if (userRating <= 0.0) BorderStroke(1.dp, Color.White.copy(alpha = 0.4f)) else null
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(
                                        if (userRating > 0.0) Icons.Filled.Star else Icons.Filled.StarBorder,
                                        contentDescription = null
                                    )
                                    Spacer(Modifier.width(6.dp))
                                    Text(
                                        text = if (userRating > 0.0) "${userRating.format1f()} / 5" else "Rate"
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // Tagline
        if (detail.tagline.isNotBlank()) {
            item {
                Text(
                    text = "\"${detail.tagline}\"",
                    color = Color.Gray,
                    style = MaterialTheme.typography.bodyMedium,
                    fontStyle = FontStyle.Italic,
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)
                )
            }
        }

        // Overview
        item {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    text = "Overview",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = detail.overview.ifBlank { "No overview available." },
                    color = Color.LightGray,
                    style = MaterialTheme.typography.bodyLarge,
                    lineHeight = 24.sp
                )
            }
        }

        // Details
        item {
            Column(
                modifier = Modifier.padding(horizontal = 20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                DetailRow(label = "Original Title", value = detail.originalTitle)
                DetailRow(label = "Language", value = detail.originalLanguage.uppercase())
                DetailRow(label = "Votes", value = detail.voteCount.toString())
            }
        }

        // Your review
        if (!userReview.isNullOrBlank()) {
            item {
                HorizontalDivider(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                    color = Color.White.copy(alpha = 0.08f)
                )
                Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)) {
                    Text(
                        text = "Your review",
                        color = Color.Gray,
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = userReview,
                        color = Color.LightGray,
                        style = MaterialTheme.typography.bodyMedium,
                        lineHeight = MaterialTheme.typography.bodyMedium.lineHeight
                    )
                }
            }
        }

        // Friends' reviews
        if (friendReviews.isNotEmpty()) {
            item {
                HorizontalDivider(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                    color = Color.White.copy(alpha = 0.08f)
                )
                Text(
                    text = "Friends' Reviews",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(start = 20.dp, end = 20.dp, bottom = 10.dp)
                )
            }
            items(friendReviews) { review ->
                FriendReviewCard(
                    review = review,
                    isLiked = review.docId in likedReviewIds,
                    onLikeClick = { onLikeReview(review) },
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                )
            }
        }

        item {
            Spacer(modifier = Modifier.height(120.dp))
        }
    }
}